import { Injectable } from '@angular/core';
import { ImageDimensions } from '../../entities/file-validation.types';
import { MIN_IMAGE_LONG_SIDE } from '../../entities/uploader.constants';
import { BrowserImageReader } from './browser-image.reader';
import { HEIF_HEAD_BYTES, readHeifDimensions } from './heif-dimension.reader';

/** The print gate every editor shares: a long side under MIN_IMAGE_LONG_SIDE is refused. Unreadable
 *  dimensions fail open — `size` null, `low` false, the file goes. */
export async function measureLongSide(
  dimensions: Pick<ImageDimensionService, 'getDimensions'>,
  file: File,
): Promise<{ readonly size: ImageDimensions | null; readonly low: boolean }> {
  const size = await dimensions.getDimensions(file).catch(() => null);
  return { size, low: size !== null && Math.max(size.width, size.height) < MIN_IMAGE_LONG_SIDE };
}

// Размеры chunk для разных форматов
const SMALL_CHUNK = 256; // Достаточно для PNG, WebP, и определения формата JPEG
const JPEG_CHUNK = 65536; // JPEG SOF может быть далеко от начала

// Magic bytes сигнатуры (первые байты)
const JPEG_MAGIC = 0xffd8;
const PNG_MAGIC = 0x89504e47;
const WEBP_RIFF = 0x52494646;

/**
 * Сервис для ультра-быстрого чтения размеров изображений.
 * Оптимизации:
 * - Минимальный chunk (256 байт) для PNG/WebP
 * - Dispatch по сигнатуре вместо перебора ридеров
 * - Inline парсинг без создания объектов
 */
@Injectable({ providedIn: 'root' })
export class ImageDimensionService {
  private readonly browserReader = new BrowserImageReader();

  /**
   * Читает размеры изображения с минимальным I/O
   */
  async getDimensions(file: File): Promise<ImageDimensions | null> {
    try {
      // Фаза 1: читаем минимальный chunk
      const smallBuffer = await file.slice(0, SMALL_CHUNK).arrayBuffer();
      const view = new DataView(smallBuffer);

      // Dispatch по magic bytes
      const magic16 = view.getUint16(0);

      // JPEG: может потребоваться больше данных
      if (magic16 === JPEG_MAGIC) {
        return this.parseJpeg(file, smallBuffer);
      }

      // PNG: размеры в первых 24 байтах
      const magic32 = view.getUint32(0);
      if (magic32 === PNG_MAGIC) {
        return this.parsePngFast(view);
      }

      // WebP: RIFF + WEBP
      if (magic32 === WEBP_RIFF && view.byteLength >= 12) {
        const webpMagic = view.getUint32(8);
        if (webpMagic === 0x57454250) {
          // "WEBP"
          return this.parseWebpFast(view);
        }
      }

      // HEIF: ftyp at offset 4. Browsers outside Safari cannot decode it, so the header is the only source.
      if (view.byteLength >= 8 && view.getUint32(4) === 0x66747970) {
        return readHeifDimensions(new DataView(await file.slice(0, HEIF_HEAD_BYTES).arrayBuffer()));
      }

      // Fallback для неизвестных форматов
      return await this.browserReader.readDimensions(file);
    } catch {
      return null;
    }
  }

  /**
   * PNG: IHDR chunk сразу после сигнатуры
   * Структура: signature(8) + length(4) + "IHDR"(4) + width(4) + height(4)
   */
  private parsePngFast(view: DataView): ImageDimensions | null {
    if (view.byteLength < 24) return null;
    // Width и height в позициях 16 и 20 (big-endian)
    return {
      width: view.getUint32(16),
      height: view.getUint32(20),
    };
  }

  /**
   * WebP: dispatch по типу chunk (VP8, VP8L, VP8X)
   */
  private parseWebpFast(view: DataView): ImageDimensions | null {
    if (view.byteLength < 30) return null;

    const chunkType =
      String.fromCharCode(view.getUint8(12)) +
      String.fromCharCode(view.getUint8(13)) +
      String.fromCharCode(view.getUint8(14)) +
      String.fromCharCode(view.getUint8(15));

    if (chunkType === 'VP8L') {
      // Lossless: offset 21, 4 bytes packed
      if (view.byteLength < 25) return null;
      const bits = view.getUint32(21, true);
      return {
        width: (bits & 0x3fff) + 1,
        height: ((bits >> 14) & 0x3fff) + 1,
      };
    }

    if (chunkType === 'VP8X') {
      // Extended: offset 24, 3+3 bytes
      if (view.byteLength < 30) return null;
      return {
        width: (view.getUint8(24) | (view.getUint8(25) << 8) | (view.getUint8(26) << 16)) + 1,
        height: (view.getUint8(27) | (view.getUint8(28) << 8) | (view.getUint8(29) << 16)) + 1,
      };
    }

    if (chunkType === 'VP8 ') {
      // Lossy: keyframe signature at offset 23
      if (view.byteLength < 30) return null;
      if (view.getUint8(23) !== 0x9d || view.getUint8(24) !== 0x01 || view.getUint8(25) !== 0x2a) {
        return null;
      }

      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }

    return null;
  }

  /**
   * JPEG: SOF маркер может быть далеко, нужен поиск
   * Сначала пробуем в маленьком буфере, потом дочитываем
   */
  private async parseJpeg(file: File, smallBuffer: ArrayBuffer): Promise<ImageDimensions | null> {
    // Попробуем найти в маленьком буфере
    const result = this.findJpegSof(new DataView(smallBuffer));
    if (result) return result;

    // Дочитываем большой chunk
    const largeBuffer = await file.slice(0, JPEG_CHUNK).arrayBuffer();
    return this.findJpegSof(new DataView(largeBuffer));
  }

  /**
   * Поиск SOF маркера в JPEG
   */
  private findJpegSof(view: DataView): ImageDimensions | null {
    let offset = 2;
    const length = view.byteLength;

    while (offset < length - 8) {
      // Ищем 0xFF
      if (view.getUint8(offset) !== 0xff) {
        offset++;
        continue;
      }

      const marker = view.getUint8(offset + 1);

      // SOF0, SOF1, SOF2 (Baseline, Extended, Progressive)
      if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
        return {
          height: view.getUint16(offset + 5),
          width: view.getUint16(offset + 7),
        };
      }

      // Пропускаем другие маркеры
      if (marker >= 0xc0 && marker <= 0xfe && marker !== 0xd8 && marker !== 0xd9) {
        const segmentLength = view.getUint16(offset + 2);
        offset += 2 + segmentLength;
      } else {
        offset += 2;
      }
    }

    return null;
  }
}
